import { cloneDeep } from 'lodash'

import AbstractRequest from './abstract'

export default class GraphRequest extends AbstractRequest {
	/**
	 * ­@constructor
	 * @param {object} service - Ravello service
	 * @param {string} node - Node name
	 * @param {string} id - Node entry ID
	 */
	constructor(service, node, id = null) {
		super()

		this._service = service
		this._node = node // Node name
		this._alias = null // Alias name
		this._parent = null // Parent
		this._filter = {} // Filter
		this._unions = [] // Request unions
		this._inclusions = [] // Request inclusions (child requests)

		this._results = null // Last query results
		if (id) {
			this._params.id = id
		}
	}

	/**
	 * Stringify
	 * @param {object} obj - Object to be stringified as query
	 * @return {string} - Stringified object
	 */
	static _toString(obj) {
		const str = JSON.stringify(obj)
		return str.replace(/"([a-z0-9_]+)":/gi, '$1:')
	}

	/**
	 * Return node name
	 * @return {string} - Node name
	 */
	get node() {
		return this._alias || this._node
	}

	/**
	 * Stringify parameters
	 * @return {string} - Stringified parameters
	 */
	_paramsToString() {
		const { fields, ...params } = this._params

		return Object.keys(params).length
			? `(${this.constructor._toString(params).replace(/^\{(.*)\}$/, '$1')})`
			: ''
	}

	/**
	 * Stringify inclusions requests
	 * @return {string} - Stringified inclusion requests
	 */
	_inclusionsToString() {
		if (!this._inclusions.length) { return '' }

		const inclusions = this._inclusions.map(inclusion => inclusion.toString())
		return ` ${inclusions.join(' ')}`
	}

	/**
	 * Stringify fields requests
	 * @return {string} - Stringified fields
	 */
	_fieldsToString() {
		if (!this._fields.length && !this._inclusions.length) { return '' }
		return ` {${this._fields.join(' ')}${this._inclusionsToString()}}`
	}

	/**
	 * Stringify union requests
	 * @return {string} - Stringified union requests
	 */
	_unionsToString() {
		if (!this._unions.length) { return '' }

		const unions = this._unions.map(union => union.toString())
		return ` ${unions.join(' ')}`
	}

	/**
	 * Union a request with this one
	 * @param {object} request - Graph request
	 * @return {object} - This
	 */
	union(request) {
		this._unions.push(request)
		return this
	}

	/**
	 * Define parent request
	 * @param {object} request - Graph parent request
	 * @return {object} - This
	 */
	parent(request) {
		request.include(this)
		return this
	}

	/**
	 * Include a request in this one
	 * @param {object} request - Graph request
	 * @return {object} - This
	 */
	include(request) {
		this._inclusions.push(request)
		request._parent = this
		return this
	}

	/**
	 * Set an alias to the request
	 * @param {string} alias - Request alias
	 * @return {object} - This
	 */
	alias(alias) {
		this._alias = alias
		return this
	}

	/**
	 * Fetch datasource version
	 * @param {string} textField - Text field
	 * @param {string} valueField - Value field
	 * @param {object} options - Additional options
	 * @param {object} options.textKey - Custom text key
	 * @param {object} options.valueKey - Custom value key
	 * @param {object} options.extraFields - Additional fields
	 * @return {object} - This
	 */
	datasource(textField = 'name', valueField = '_id', {
		textKey = 'text',
		valueKey = 'value',
		extraFields = [],
	} = {}) {
		this._params.datasource = true
		this._fields = [`${textKey}: ${textField}`, `${valueKey}: ${valueField}`, ...extraFields]
		return this
	}

	/**
	 * Search text in collection
	 * @param {string} text - Text to search
	 * @param {boolean} sensitive - Whether to make a case sensitive search
	 * @return {object} - This
	 */
	search(text, sensitive = false) {
		this._params.search = text
		this._params.sensitive = sensitive

		return this
	}

	/**
	 * Inject count request
	 * @param {object} request - Graph request
	 * @return {object} - This
	 */
	count(alias = null) {
		const request = new GraphRequest(this._service, `${this._node}Count`)
			.setFilter(this._filter)
		if (alias) {
			request.alias(alias)
		}
		return this.union(request)
	}

	/**
	 * Add filter
	 * @param {string} name - Filter name
	 * @param {any} value - Filter value
	 * @return {object} - This
	 */
	filter(name, value) {
		this._filter[name] = value
		this._params.filter = this._filter
		return this
	}

	/**
	 * Set filter
	 * @param {object} filter - Filter
	 * @return {object} - This
	 */
	setFilter(filter) {
		this._filter = cloneDeep(filter)
		this._params.filter = this._filter
		return this
	}

	/**
	 * Stringify request to GraphQL request string
	 * @return {string} - Stringified query
	 */
	toString() {
		const prefix = this._alias ? `${this._alias}: ${this._node}` : this._node
		return `${prefix}${this._paramsToString()}${this._fieldsToString()}${this._unionsToString()}`
	}

	/* Query to fetch details about a type
		query {
		__type(name: "Query") {
			fields {
				name
				type {
					name
					ofType {
						name
					}
				}
			}
		}
	}
	*/

	/**
	 * Execute query
	 * @param {boolean} returnAll - Return all results instead of node results
	 * @return {object|array} - Node/All results
	 */
	async query(returnAll = false) {
		if (this._parent) {
			await this._parent.query(returnAll)

			if (returnAll) { return this.results }
			return this.results[this.node]
		}

		const query = `query {${this}}`
		const res = await this._service.graphql({ query })
		this._results = res.data || {}

		if (returnAll) { return this._results }
		return this._results[this.node]
	}

	/**
	 * Get results from last query response
	 * @return {object} - Last query results
	 */
	get results() {
		if (this._parent) {
			const { results } = this._parent
			if (!results) { return null }
			return results[this._parent.node]
		}

		return this._results
	}
}
